import React, { useMemo, useEffect, useState } from "react";
import Sidebar from "../components/layout/Sidebar";
import Topbar from "../components/layout/Topbar";
import UserPreviewCard from "../components/matchs/UserPreviewCard";

import { generateWeeklySuggestions } from "../services/matchEngine";
import { getPlanLabel } from "../services/mockUsersService";

import { db } from "../services/firebase";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";

export default function Matchs() {
  // ============================================================
  // 🔥 Carregar usuários do Firestore
  // ============================================================
  const [users, setUsers] = useState([]);

  useEffect(() => {
    async function loadUsers() {
      try {
        const snap = await getDocs(collection(db, "users"));
        const list = snap.docs.map((d) => {
          const data = d.data();
          const authUid = (data.uid || data.authUid || data.userId || d.id)?.toString();

          return {
            id: d.id.toString(),
            authUid,

            // ===== usados na UI =====
            name: data.name || data.displayName || "Sem nome",
            city: data.city || data.cidade || "Indefinido",
            plan: data.plan || data.plano || "free",
            photoURL:
              data.foto ||
              data.verifySelfieUrl ||
              data.imageUrl ||
              data.photoURL ||
              "/default-avatar.png",

            // ==========================================================
            // ✅ CAMPOS IMPORTANTES PARA O MATCH ENGINE (pra IA funcionar)
            // ==========================================================
            status: (data.status || data.accountStatus || "ativo").toString(), // 🔥 FIX
            gender: (data.gender || data.genero || "").toString(),
            searchGender: (data.searchGender || data.procuraGenero || "ambos").toString(),
            acceptsOtherCities: !!(data.acceptsOtherCities ?? data.aceitaOutrasCidades ?? false),

            // Datas — engine espera YYYY-MM-DD
            birthdate: (data.birthdate || data.dataNascimento || "").toString(),

            // Preferências — mantém vazio se não existir ainda
            lifestyleTags: Array.isArray(data.lifestyleTags) ? data.lifestyleTags : [],
            relationshipPurpose: (data.relationshipPurpose || "").toString(),
            valuesImportant: Array.isArray(data.valuesImportant) ? data.valuesImportant : [],
            valuesEssential: Array.isArray(data.valuesEssential) ? data.valuesEssential : [],
            desiredLifestyle: Array.isArray(data.desiredLifestyle) ? data.desiredLifestyle : [],
            nonNegotiables: Array.isArray(data.nonNegotiables) ? data.nonNegotiables : [],
            blockedUsers: Array.isArray(data.blockedUsers) ? data.blockedUsers : [],
          };
        });

        setUsers(list);
      } catch (err) {
        console.error("Erro ao carregar usuários:", err);
      }
    }
    loadUsers();
  }, []);

  // ============================================================
  // 🔔 NOTIFICAÇÕES (ALINHADAS COM O FLUTTER)
  // - DEDUPE com IDs fixos (setDoc) pra nunca spam
  // ============================================================
  const createNotificationJob = async ({ jobId, title, message, data = {} }) => {
    const jobRef = doc(db, "notifications_jobs", jobId);
    await setDoc(
      jobRef,
      {
        title: (title || "").toString(),
        message: (message || "").toString(),
        data,
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const upsertOutboxNotification = async ({
    outboxId,
    targetUid,
    title,
    message,
    jobId,
    mode = "user",
    topic = "",
    data = {},
  }) => {
    const ref = doc(db, "notifications_outbox", outboxId);

    await setDoc(
      ref,
      {
        targetUserId: targetUid,
        targetUid: targetUid,
        status: "pending",
        title: (title || "").toString(),
        message: (message || "").toString(),
        jobId: (jobId || "").toString(),
        mode: (mode || "").toString(),
        topic: (topic || "").toString(),
        data,
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const notifyMatchSuggestion = async ({
    suggestionId,
    userA,
    userB,
    scorePercent,
    reason,
  }) => {
    const uidA = (userA?.authUid || userA?.id || "").toString();
    const uidB = (userB?.authUid || userB?.id || "").toString();
    if (!uidA || !uidB) {
      console.warn("⚠️ notifyMatchSuggestion: UIDs inválidos", { uidA, uidB, userA, userB });
      return;
    }

    const jobId = `job_match_suggestion_${suggestionId}`;

    const title = "💞 Novo match sugerido!";
    const msgA =
      `Temos uma sugestão pra você com ${userB?.name || "alguém especial"}. ` +
      (scorePercent != null ? `Compatibilidade: ${scorePercent}%.` : "");
    const msgB =
      `Temos uma sugestão pra você com ${userA?.name || "alguém especial"}. ` +
      (scorePercent != null ? `Compatibilidade: ${scorePercent}%.` : "");

    await createNotificationJob({
      jobId,
      title,
      message: `${msgA}\n${msgB}`,
      data: {
        type: "match_suggestion",
        suggestionId,
        userAId: userA?.id || "",
        userBId: userB?.id || "",
        userAUid: uidA,
        userBUid: uidB,
        scorePercent: scorePercent ?? null,
        reason: reason || "",
      },
    });

    await upsertOutboxNotification({
      outboxId: `notif_match_suggestion_${suggestionId}_${uidA}`,
      targetUid: uidA,
      title,
      message: msgA,
      jobId,
      data: {
        type: "match_suggestion",
        suggestionId,
        otherUserUid: uidB,
        otherUserName: userB?.name || "",
        scorePercent: scorePercent ?? null,
        reason: reason || "",
      },
    });

    await upsertOutboxNotification({
      outboxId: `notif_match_suggestion_${suggestionId}_${uidB}`,
      targetUid: uidB,
      title,
      message: msgB,
      jobId,
      data: {
        type: "match_suggestion",
        suggestionId,
        otherUserUid: uidA,
        otherUserName: userA?.name || "",
        scorePercent: scorePercent ?? null,
        reason: reason || "",
      },
    });
  };

  const notifyMatchConfirmed = async ({ matchId, userA, userB }) => {
    const uidA = (userA?.authUid || userA?.id || "").toString();
    const uidB = (userB?.authUid || userB?.id || "").toString();
    if (!uidA || !uidB) {
      console.warn("⚠️ notifyMatchConfirmed: UIDs inválidos", { uidA, uidB, userA, userB });
      return;
    }

    const jobId = `job_match_confirmed_${matchId}`;
    const title = "✅ Match confirmado!";
    const msgA = `Seu match com ${userB?.name || "usuário(a)"} foi confirmado.`;
    const msgB = `Seu match com ${userA?.name || "usuário(a)"} foi confirmado.`;

    await createNotificationJob({
      jobId,
      title,
      message: `${msgA}\n${msgB}`,
      data: {
        type: "match_confirmed",
        matchId,
        userAUid: uidA,
        userBUid: uidB,
        userAId: userA?.id || "",
        userBId: userB?.id || "",
      },
    });

    await upsertOutboxNotification({
      outboxId: `notif_match_confirmed_${matchId}_${uidA}`,
      targetUid: uidA,
      title,
      message: msgA,
      jobId,
      data: {
        type: "match_confirmed",
        matchId,
        otherUserUid: uidB,
        otherUserName: userB?.name || "",
      },
    });

    await upsertOutboxNotification({
      outboxId: `notif_match_confirmed_${matchId}_${uidB}`,
      targetUid: uidB,
      title,
      message: msgB,
      jobId,
      data: {
        type: "match_confirmed",
        matchId,
        otherUserUid: uidA,
        otherUserName: userA?.name || "",
      },
    });
  };

  // ============================================================
  // IA local — sugestões automáticas
  // ============================================================
  const suggestedMatchesIA = useMemo(() => {
    if (!users.length) return [];

    // ✅ Agora vai funcionar porque users tem status/gênero/etc.
    const raw = generateWeeklySuggestions(users, {
      minScore: 40, // mantém seu padrão
    });

    return raw.map((m) => {
      const userA = users.find((u) => u.id === m.userA);
      const userB = users.find((u) => u.id === m.userB);

      const city =
        userA?.city && userB?.city
          ? userA.city === userB.city
            ? userA.city
            : `${userA.city} / ${userB.city}`
          : "Indefinido";

      return {
        id: m.id,
        userA,
        userB,
        userAId: userA?.id,
        userBId: userB?.id,
        score: m.score ?? null,
        reason: m.reason ?? "",
        city,
        planLabel: `${getPlanLabel(userA?.plan)} / ${getPlanLabel(userB?.plan)}`,
        originLabel: "Sugestão IA",
        createdAt: m.createdAt || new Date(),
        status: "suggested",
        curated: false,
      };
    });
  }, [users]);

  const [pendingMatchs, setPendingMatchs] = useState([]);
  const [confirmedMatchs, setConfirmedMatchs] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);

  // ============================================================
  // Curadoria Manual
  // ============================================================
  const [manualUserA, setManualUserA] = useState("");
  const [manualUserB, setManualUserB] = useState("");
  const [manualScore, setManualScore] = useState(80);
  const [manualReason, setManualReason] = useState("");
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState("");
  const [manualSuccess, setManualSuccess] = useState("");

  // ✅ trava ações pra evitar clique múltiplo
  const [busyIds, setBusyIds] = useState(() => new Set());

  const setBusy = (id, value) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (value) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const isBusy = (id) => busyIds.has(id);

  // ============================================================
  // Carregar sugestões pendentes e confirmadas
  // ============================================================
  useEffect(() => {
    async function loadMatches() {
      if (!users.length) return;
      try {
        // Carregar pendentes
        const suggestionsSnap = await getDocs(collection(db, "suggestions_week"));
        const pendentes = [];

        suggestionsSnap.forEach((docSnap) => {
          const data = docSnap.data();
          const rawA = (data.userAId ?? data.userAUid ?? data.userA)?.toString();
          const rawB = (data.userBId ?? data.userBUid ?? data.userB)?.toString();

          const userA = users.find((u) => u.id === rawA) || users.find((u) => u.authUid === rawA);
          const userB = users.find((u) => u.id === rawB) || users.find((u) => u.authUid === rawB);

          if (!userA || !userB) return;

          pendentes.push({
            id: docSnap.id,
            userA,
            userB,
            city:
              userA.city && userB.city
                ? userA.city === userB.city
                  ? userA.city
                  : `${userA.city} / ${userB.city}`
                : "Indefinido",
            planLabel: `${getPlanLabel(userA.plan)} / ${getPlanLabel(userB.plan)}`,
            originLabel: data.originLabel || "Curadoria Manual",
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            status: "pending",
            curated: true,
          });
        });

        setPendingMatchs(pendentes);

        // Carregar confirmados
        const matchesSnap = await getDocs(collection(db, "matches"));
        const confirmados = [];

        matchesSnap.forEach((docSnap) => {
          const data = docSnap.data();
          const uidA = data.userAUid || data.users?.[0];
          const uidB = data.userBUid || data.users?.[1];

          const userA = users.find((u) => u.authUid === uidA) || users.find((u) => u.id === uidA);
          const userB = users.find((u) => u.authUid === uidB) || users.find((u) => u.id === uidB);

          if (!userA || !userB) return;

          confirmados.push({
            id: docSnap.id,
            userA,
            userB,
            city:
              userA.city && userB.city
                ? userA.city === userB.city
                  ? userA.city
                  : `${userA.city} / ${userB.city}`
                : "Indefinido",
            planLabel: `${getPlanLabel(userA.plan)} / ${getPlanLabel(userB.plan)}`,
            originLabel: data.origin || "Match Confirmado",
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            status: "confirmed",
          });
        });

        setConfirmedMatchs(confirmados);
      } catch (err) {
        console.error("Erro ao carregar matches:", err);
      }
    }
    loadMatches();
  }, [users]);

  // ============================================================
  // Funções de ação
  // ============================================================
  const handleConfirmMatch = async (matchId) => {
    if (isBusy(matchId)) return;

    setBusy(matchId, true);
    try {
      const match = pendingMatchs.find((m) => m.id === matchId);
      if (!match) return;

      const uidA = (match.userA.authUid || match.userA.id).toString();
      const uidB = (match.userB.authUid || match.userB.id).toString();

      const matchDocId = `match_${uidA}_${uidB}`;
      const matchRef = doc(db, "matches", matchDocId);

      const existing = await getDoc(matchRef);
      if (existing.exists()) {
        await deleteDoc(doc(db, "suggestions_week", matchId));
        setPendingMatchs((prev) => prev.filter((m) => m.id !== matchId));
        alert("Esse match já estava confirmado. (evitamos notificação duplicada)");
        return;
      }

      await setDoc(matchRef, {
        users: [uidA, uidB],
        userAUid: uidA,
        userBUid: uidB,
        userAId: match.userA.id,
        userBId: match.userB.id,
        origin: "manual",
        sourceSuggestionId: match.id,
        status: "confirmed",
        createdAt: serverTimestamp(),
      });

      await notifyMatchConfirmed({
        matchId: matchDocId,
        userA: match.userA,
        userB: match.userB,
      });

      await deleteDoc(doc(db, "suggestions_week", matchId));

      setPendingMatchs((prev) => prev.filter((m) => m.id !== matchId));
      setConfirmedMatchs((prev) => [
        {
          ...match,
          id: matchDocId,
          status: "confirmed",
          originLabel: "Match Confirmado",
        },
        ...prev,
      ]);

      alert("Match confirmado com sucesso!");
    } catch (err) {
      console.error("Erro ao confirmar match:", err);
      alert("Erro ao confirmar match");
    } finally {
      setBusy(matchId, false);
    }
  };

  const handleCancelMatch = async (matchId) => {
    if (isBusy(matchId)) return;

    setBusy(matchId, true);
    try {
      await deleteDoc(doc(db, "suggestions_week", matchId));
      setPendingMatchs((prev) => prev.filter((m) => m.id !== matchId));
      alert("Sugestão cancelada com sucesso!");
    } catch (err) {
      console.error("Erro ao cancelar match:", err);
      alert("Erro ao cancelar sugestão");
    } finally {
      setBusy(matchId, false);
    }
  };

  const handleCreateManualSuggestion = async () => {
    setManualError("");
    setManualSuccess("");

    if (!manualUserA || !manualUserB) {
      setManualError("Selecione Usuário A e Usuário B.");
      return;
    }
    if (manualUserA === manualUserB) {
      setManualError("Usuário A e Usuário B não podem ser o mesmo.");
      return;
    }

    const userA = users.find((u) => u.id === manualUserA);
    const userB = users.find((u) => u.id === manualUserB);
    if (!userA || !userB) {
      setManualError("Usuários inválidos.");
      return;
    }

    const uidA = (userA.authUid || userA.id || "").toString();
    const uidB = (userB.authUid || userB.id || "").toString();

    if (!uidA || !uidB) {
      setManualError("Não foi possível identificar authUid dos usuários.");
      return;
    }

    setManualLoading(true);
    try {
      const docId = `manual_${Date.now()}_${userA.id}_${userB.id}`;
      const ref = doc(db, "suggestions_week", docId);

      await setDoc(ref, {
        userAId: userA.id,
        userBId: userB.id,
        userAUid: uidA,
        userBUid: uidB,
        score: manualScore / 100,
        reason: manualReason,
        curated: true,
        origin: "manual",
        originLabel: "Curadoria Manual",
        createdAt: serverTimestamp(),
      });

      await notifyMatchSuggestion({
        suggestionId: docId,
        userA,
        userB,
        scorePercent: Number.isFinite(manualScore) ? manualScore : null,
        reason: manualReason,
      });

      setManualSuccess("✅ Sugestão criada e notificação enviada!");

      const newPending = {
        id: docId,
        userA,
        userB,
        city:
          userA.city && userB.city
            ? userA.city === userB.city
              ? userA.city
              : `${userA.city} / ${userB.city}`
            : "Indefinido",
        planLabel: `${getPlanLabel(userA.plan)} / ${getPlanLabel(userB.plan)}`,
        originLabel: "Curadoria Manual",
        createdAt: new Date(),
        status: "pending",
        curated: true,
      };

      setPendingMatchs((prev) => [newPending, ...prev]);

      setTimeout(() => {
        setManualUserA("");
        setManualUserB("");
        setManualReason("");
        setManualScore(80);
        setManualSuccess("");
      }, 3000);
    } catch (e) {
      console.error("Erro ao salvar:", e);
      setManualError("Erro ao salvar/notificar no Firestore.");
    } finally {
      setManualLoading(false);
    }
  };

  // ============================================================
  // UI Helpers
  // ============================================================
  const userAData = users.find((u) => u.id === manualUserA);
  const userBData = users.find((u) => u.id === manualUserB);

  const compatibilidade =
    manualUserA && manualUserB
      ? Math.min(100, Math.max(0, manualScore + Math.floor(Math.random() * 10 - 5)))
      : null;

  // ============================================================
  // UI
  // ============================================================
  return (
    <div style={styles.pageWrapper}>
      <Sidebar />
      <div style={styles.pageContent}>
        <Topbar />

        <div style={styles.matchsPage}>
          <div style={styles.header}>
            <h1 style={styles.title}>One Love</h1>
            <p style={styles.subtitle}>
              Acompanhe e gerencie os matches sugeridos, pendentes e confirmados.
            </p>
          </div>

          <div style={styles.columnsGrid}>
            {/* Coluna 1 */}
            <div style={styles.column}>
              <div style={styles.columnHeader}>
                <h3 style={styles.columnTitle}>Sugeridos pela IA</h3>
              </div>
              <div style={styles.columnContent}>
                {suggestedMatchesIA.length > 0 ? (
                  suggestedMatchesIA.slice(0, 5).map((match) => (
                    <div key={match.id} style={styles.matchCard}>
                      <div style={styles.matchUsers}>
                        <div style={styles.userInfo}>
                          <div style={styles.userLabel}>USUÁRIO A</div>
                          <div style={styles.userName}>{match.userA?.name || "Não disponível"}</div>
                        </div>
                        <div style={styles.userInfo}>
                          <div style={styles.userLabel}>USUÁRIO B</div>
                          <div style={styles.userName}>{match.userB?.name || "Não disponível"}</div>
                        </div>
                      </div>
                      <div style={styles.matchDetails}>
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>CIDADE:</span>
                          <span style={styles.detailValue}>{match.city}</span>
                        </div>
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>PLANO:</span>
                          <span style={styles.detailValue}>{match.planLabel}</span>
                        </div>
                      </div>
                      <button style={styles.actionButton} onClick={() => setSelectedMatch(match)}>
                        Ver Detalhes
                      </button>
                    </div>
                  ))
                ) : (
                  <div style={styles.emptyMessage}>Nenhum registro nesta aba no momento.</div>
                )}
              </div>
            </div>

            {/* Coluna 2 */}
            <div style={styles.column}>
              <div style={{ ...styles.columnHeader, background: "#ffc107" }}>
                <h3 style={styles.columnTitle}>Pendentes de confirmação</h3>
              </div>
              <div style={styles.columnContent}>
                {pendingMatchs.length > 0 ? (
                  pendingMatchs.slice(0, 5).map((match) => {
                    const busy = isBusy(match.id);
                    return (
                      <div key={match.id} style={styles.matchCard}>
                        <div style={styles.matchUsers}>
                          <div style={styles.userInfo}>
                            <div style={styles.userLabel}>USUÁRIO A</div>
                            <div style={styles.userName}>{match.userA?.name || "Não disponível"}</div>
                          </div>
                          <div style={styles.userInfo}>
                            <div style={styles.userLabel}>USUÁRIO B</div>
                            <div style={styles.userName}>{match.userB?.name || "Não disponível"}</div>
                          </div>
                        </div>
                        <div style={styles.matchDetails}>
                          <div style={styles.detailRow}>
                            <span style={styles.detailLabel}>CIDADE:</span>
                            <span style={styles.detailValue}>{match.city}</span>
                          </div>
                          <div style={styles.detailRow}>
                            <span style={styles.detailLabel}>PLANO:</span>
                            <span style={styles.detailValue}>{match.planLabel}</span>
                          </div>
                        </div>
                        <div style={styles.actionButtons}>
                          <button
                            disabled={busy}
                            style={{
                              ...styles.actionButton,
                              ...styles.confirmButton,
                              ...(busy ? styles.disabledButton : {}),
                            }}
                            onClick={() => handleConfirmMatch(match.id)}
                          >
                            {busy ? "Processando..." : "Confirmar"}
                          </button>
                          <button
                            disabled={busy}
                            style={{
                              ...styles.actionButton,
                              ...styles.cancelButton,
                              ...(busy ? styles.disabledButton : {}),
                            }}
                            onClick={() => handleCancelMatch(match.id)}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={styles.emptyMessage}>Nenhum registro pendente.</div>
                )}
              </div>
            </div>

            {/* Coluna 3 */}
            <div style={styles.column}>
              <div style={{ ...styles.columnHeader, background: "#28a745" }}>
                <h3 style={styles.columnTitle}>Matches confirmados</h3>
              </div>
              <div style={styles.columnContent}>
                {confirmedMatchs.length > 0 ? (
                  confirmedMatchs.slice(0, 5).map((match) => (
                    <div key={match.id} style={styles.matchCard}>
                      <div style={styles.matchUsers}>
                        <div style={styles.userInfo}>
                          <div style={styles.userLabel}>USUÁRIO A</div>
                          <div style={styles.userName}>{match.userA?.name || "Não disponível"}</div>
                        </div>
                        <div style={styles.userInfo}>
                          <div style={styles.userLabel}>USUÁRIO B</div>
                          <div style={styles.userName}>{match.userB?.name || "Não disponível"}</div>
                        </div>
                      </div>
                      <div style={styles.matchDetails}>
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>ORIGEM:</span>
                          <span style={styles.detailValue}>{match.originLabel}</span>
                        </div>
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>DATA:</span>
                          <span style={styles.detailValue}>
                            {match.createdAt?.toLocaleDateString("pt-BR") || "N/A"}
                          </span>
                        </div>
                      </div>
                      <div style={styles.actionButtons}>
                        <button style={{ ...styles.actionButton, ...styles.detailsButton }}>
                          AÇÕES
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={styles.emptyMessage}>Nenhum match confirmado.</div>
                )}
              </div>
            </div>
          </div>

          {/* Curadoria Manual */}
          <div style={styles.manualSection}>
            <div style={styles.manualHeader}>
              <h3 style={styles.manualTitle}>Curadoria Manual</h3>
            </div>
            <div style={styles.manualContent}>
              {manualError && <div style={styles.errorAlert}>{manualError}</div>}
              {manualSuccess && <div style={styles.successAlert}>{manualSuccess}</div>}

              <div style={styles.formLayout}>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>USUÁRIO A</label>
                    <select
                      value={manualUserA}
                      onChange={(e) => setManualUserA(e.target.value)}
                      style={styles.formSelect}
                    >
                      <option value="">Selecione um usuário</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>USUÁRIO B</label>
                    <select
                      value={manualUserB}
                      onChange={(e) => setManualUserB(e.target.value)}
                      style={styles.formSelect}
                    >
                      <option value="">Selecione um usuário</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={styles.previewRow}>
                  {userAData && (
                    <div style={styles.previewColumn}>
                      <UserPreviewCard user={userAData} />
                    </div>
                  )}
                  {userBData && (
                    <div style={styles.previewColumn}>
                      <UserPreviewCard user={userBData} />
                    </div>
                  )}
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>CIDADE</label>
                    <input
                      type="text"
                      value={userAData?.city || "Indefinido"}
                      disabled
                      style={styles.formInput}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>PLANO</label>
                    <input
                      type="text"
                      value={
                        userAData && userBData
                          ? `${getPlanLabel(userAData.plan)} / ${getPlanLabel(userBData.plan)}`
                          : "Indefinido"
                      }
                      disabled
                      style={styles.formInput}
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>ORIGEM</label>
                    <input type="text" value="Curadoria Manual" disabled style={styles.formInput} />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>SCORE (0-100)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={manualScore}
                      onChange={(e) => setManualScore(Number(e.target.value))}
                      style={styles.formInput}
                    />
                  </div>
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroupFull}>
                    <label style={styles.formLabel}>MOTIVO DA CURADORIA</label>
                    <select
                      value={manualReason}
                      onChange={(e) => setManualReason(e.target.value)}
                      style={styles.formSelect}
                    >
                      <option value="">Selecione uma mensagem</option>
                      <option value="Vocês têm uma energia parecida 🌟">Vocês têm uma energia parecida 🌟</option>
                      <option value="Sentimos uma conexão especial entre vocês 💞">Sentimos uma conexão especial entre vocês 💞</option>
                      <option value="Vocês compartilham sonhos e valores em comum ✨">Vocês compartilham sonhos e valores em comum ✨</option>
                      <option value="Parece um ótimo equilíbrio de personalidades 💫">Parece um ótimo equilíbrio de personalidades 💫</option>
                      <option value="Uma combinação com muito potencial! 💐">Uma combinação com muito potencial! 💐</option>
                    </select>
                  </div>
                </div>

                {compatibilidade && (
                  <div style={styles.compatibilitySection}>
                    <div style={styles.compatibilityInfo}>
                      💞 Compatibilidade estimada: <strong>{compatibilidade}%</strong>
                    </div>
                    <div style={styles.compatibilityBar}>
                      <div style={{ ...styles.compatibilityFill, width: `${compatibilidade}%` }} />
                    </div>
                  </div>
                )}

                <div style={styles.formActions}>
                  <button
                    onClick={handleCreateManualSuggestion}
                    disabled={manualLoading}
                    style={{
                      ...styles.saveButton,
                      ...(manualLoading ? styles.disabledButton : {}),
                    }}
                  >
                    {manualLoading ? "Salvando..." : "Salvar + Notificar"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* selectedMatch (se você tiver modal, mantém aqui) */}
        </div>
      </div>
    </div>
  );
}

const styles = {
  pageWrapper: {
    display: "flex",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
  },
  pageContent: {
    flex: 1,
    padding: "24px",
    overflowY: "auto",
  },
  matchsPage: {
    maxWidth: "1400px",
    margin: "0 auto",
  },
  header: {
    marginBottom: "32px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e0e0e0",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#ff4f84",
    margin: "0 0 8px 0",
  },
  subtitle: {
    fontSize: "15px",
    color: "#666",
    margin: "0",
  },
  columnsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "24px",
    marginBottom: "40px",
  },
  column: {
    background: "white",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  columnHeader: {
    background: "#ff4f84",
    color: "white",
    padding: "16px 20px",
  },
  columnTitle: {
    margin: "0",
    fontSize: "16px",
    fontWeight: "600",
  },
  columnContent: {
    padding: "20px",
    maxHeight: "500px",
    overflowY: "auto",
  },
  matchCard: {
    background: "#f9f9f9",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px",
    borderLeft: "4px solid #ff4f84",
  },
  matchUsers: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  userInfo: {
    flex: 1,
  },
  userLabel: {
    fontSize: "11px",
    color: "#ff4f84",
    fontWeight: "600",
    textTransform: "uppercase",
    marginBottom: "4px",
  },
  userName: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#333",
  },
  matchDetails: {
    marginBottom: "12px",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "12px",
    padding: "4px 0",
  },
  detailLabel: {
    color: "#666",
    fontWeight: "500",
  },
  detailValue: {
    color: "#333",
    fontWeight: "600",
  },
  actionButton: {
    padding: "8px 16px",
    borderRadius: "6px",
    border: "none",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s",
    width: "100%",
  },
  confirmButton: {
    background: "#28a745",
    color: "white",
  },
  cancelButton: {
    background: "#dc3545",
    color: "white",
  },
  detailsButton: {
    background: "#6c757d",
    color: "white",
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
  },
  emptyMessage: {
    textAlign: "center",
    color: "#999",
    padding: "40px 20px",
    fontSize: "14px",
  },
  manualSection: {
    background: "white",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  manualHeader: {
    background: "#ff4f84",
    color: "white",
    padding: "16px 24px",
  },
  manualTitle: {
    margin: "0",
    fontSize: "16px",
    fontWeight: "600",
  },
  manualContent: {
    padding: "24px",
  },
  formLayout: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  formGroupFull: {
    gridColumn: "span 2",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  formLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#ff4f84",
    textTransform: "uppercase",
  },
  formSelect: {
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    background: "white",
    color: "#333",
  },
  formInput: {
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    background: "white",
    color: "#333",
  },
  previewRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
  },
  previewColumn: {
    display: "flex",
    justifyContent: "center",
  },
  compatibilitySection: {
    marginTop: "10px",
  },
  compatibilityInfo: {
    textAlign: "center",
    fontSize: "14px",
    marginBottom: "8px",
    color: "#333",
  },
  compatibilityBar: {
    height: "8px",
    background: "#f0f0f0",
    borderRadius: "4px",
    overflow: "hidden",
  },
  compatibilityFill: {
    height: "100%",
    background: "linear-gradient(90deg, #ff4f84, #ff84a6)",
    borderRadius: "4px",
    transition: "width 0.3s ease",
  },
  formActions: {
    marginTop: "20px",
  },
  saveButton: {
    width: "100%",
    padding: "14px",
    background: "#ff4f84",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  disabledButton: {
    opacity: 0.65,
    cursor: "not-allowed",
  },
  errorAlert: {
    background: "#ffe5ee",
    border: "1px solid #ffb3c8",
    color: "#b1003a",
    padding: "12px 16px",
    borderRadius: "8px",
    marginBottom: "20px",
    fontWeight: "600",
  },
  successAlert: {
    background: "#eafff2",
    border: "1px solid #86e5b7",
    color: "#0b6b3a",
    padding: "12px 16px",
    borderRadius: "8px",
    marginBottom: "20px",
    fontWeight: "600",
  },
};
